import {Injectable} from "@angular/core";
import {UserSessionRepository} from "../repositories/user-session.repository";
import moment from "moment";
import 'moment-duration-format';
import {DateTimeF} from "../interfaces/rest/DateTimeF";
import {TranslateService} from "@ngx-translate/core";
import {UserSession} from "../interfaces/domain/UserSession";

@Injectable()
export class CustomDatesService {

  protected daysTranslation = 'days';
  protected hoursTranslation = 'hours';
  protected minutesTranslation = 'minutes';
  protected secondsTranslation = 'seconds';
  protected daysTranslationS = 'd';
  protected hoursTranslationS = 'h';
  protected minutesTranslationS = 'm';
  protected secondsTranslationS = 's';

  constructor(private userSessionRepository: UserSessionRepository,
              private translateService: TranslateService) {
    this.initTranslations();
  }

  protected initTranslations() {
    this.translateService.get('t.commonTimeUnitDays').subscribe((value) => this.daysTranslation = value);
    this.translateService.get('t.commonTimeUnitHours').subscribe((value) => this.hoursTranslation = value);
    this.translateService.get('t.commonTimeUnitMinutes').subscribe((value) => this.minutesTranslation = value);
    this.translateService.get('t.commonTimeUnitSeconds').subscribe((value) => this.secondsTranslation = value);
    this.translateService.get('t.commonShortTimeUnitDays').subscribe((value) => this.daysTranslationS = value);
    this.translateService.get('t.commonShortTimeUnitHours').subscribe((value) => this.hoursTranslationS = value);
    this.translateService.get('t.commonShortTimeUnitMinutes').subscribe((value) => this.minutesTranslationS = value);
    this.translateService.get('t.commonShortTimeUnitSeconds').subscribe((value) => this.secondsTranslationS = value);
  }

  convertMillisToUserFormat(millis: number|null|undefined): string {
    if(!millis) {
      return '';
    }

    const datetime = moment(millis);
    return this.convertDateTimeToUserFormat(datetime);
  }

  convertMillisToLocal(millis: number, utcOffset: number, outputFormat?: string) {
    const date = moment(millis).utc();
    date.add(utcOffset, 'minutes');

    if(outputFormat && outputFormat.trim()) {
      return date.format(outputFormat);
    }
    else {
      return date;
    }
  }

  convertDateTimeToUserFormat(datetime: any): string {
    const userSession = this.userSessionRepository.getUserSession();

    if(userSession && userSession.dateConfigObject && userSession.timeConfigObject) {
      const dateString = this.formatMomentDateTime(datetime, <DateTimeF>userSession.dateConfigObject, 'date');
      const timeString = this.formatMomentDateTime(datetime, <DateTimeF>userSession.timeConfigObject, 'time');
      return dateString + ' ' + timeString;
    }
    else {
      return this.getJsDefaultDateTimeString(moment(datetime).toDate());
    }
  }

  getCurrentTimeInShortFormat(userSession: UserSession): string {
    const date = new Date();

    if(userSession.timeConfigObject) {
      const dateTimeConf = userSession.timeConfigObject;

      if(dateTimeConf && dateTimeConf.hasOwnProperty('dateTimeFormat')) {
        const shortTimeFormat = dateTimeConf.dateTimeFormat.replace(':ss', '');
        return moment(date).format(shortTimeFormat);
      }
    }

    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatCurrentHoursTime(userSession: UserSession, date: any) {
    if(userSession && userSession.dateConfigObject && userSession.timeConfigObject) {
      const newDate = moment(date, 'hh:mm:ss');
      return this.formatMomentDateTime(newDate, userSession.timeConfigObject, 'time');
    }
    else {
      return this.getJsDefaultDateTimeString(date);
    }
  }

  getJsDefaultDateTimeString(datetime: Date): string {
    return datetime.toLocaleDateString() + ' ' + datetime.toLocaleTimeString();
  }

  formatMomentDateTime(datetime: any, dateTimeConf: DateTimeF, type: 'date'|'time'): string {
    const dtFormat = dateTimeConf ? DateTimeF.getMomentFormat(dateTimeConf) : null;

    if(dtFormat) {
      return moment(datetime).format(dtFormat);
    }
    else {
      if(type === 'date') {
        return moment(datetime).toDate().toLocaleDateString();
      }
      else if(type === 'time') {
        return moment(datetime).toDate().toLocaleTimeString();
      }
      else {
        return this.getJsDefaultDateTimeString(moment(datetime).toDate());
      }
    }
  }

  getCurrentDateTimeFormatted(format: string) {
    return moment().format(format);
  }

  humaniseSecondsWithTranslation(seconds: number, format: string = 'normal', simplify: boolean = false): string {
    let result;

    if (format === 'normal') {
      result = moment.duration(seconds, 'seconds').format(`d[${this.daysTranslationS}] h[${this.hoursTranslationS}] m[${this.minutesTranslationS}] s[${this.secondsTranslationS}]`, {trim: 'right'});
    } else if (format === 'short') {
      result = moment.duration(seconds, 'seconds').format('hh:mm:ss', {trim: false});
    } else if (format === 'hours_seconds') {
      result = moment.duration(seconds, 'seconds').format('hh:mm', {trim: false});
    } else {
      result = moment.duration(seconds, 'seconds').format(`d[${this.daysTranslationS}], h[${this.hoursTranslationS}], m[${this.minutesTranslationS}], s[${this.secondsTranslationS}]`, {trim: 'right'});
    }

    if (simplify && format !== 'short') {
      let tokens = result.split(', ');
      tokens = tokens.filter(token => {
        if (token.indexOf('0') === 0) {
          return false;
        }
        return true;
      });

      if (tokens.length > 2) {
        tokens = tokens.splice(0, 2);
      }

      result = '';
      for (let i = 0; i < tokens.length; i++) {
        result += tokens[i];
        if ((i + 1) < tokens.length) {
          result += ', ';
        }
      }
    }

    return result;
  }

  getTimestampInMillis(timestamp: string): number {
    if (!timestamp || timestamp === 'NA') {
      return 0;
    }

    return moment.utc(timestamp).local().valueOf();
  }

  getDateTimeToUTCFormatted(time: Date, format : string) {
    return moment(time).utc().format(format);
  }

  /**
   * Convert date from UTC timezone to a specific offset (minutes) date.
   * @param date - UTC date. Should be formatted for moment.js parsing.
   * @param dateFormat - moment.js date format string/unix, used for input/output.
   * @param utcOffset - UTC offset in minutes.
   * @param outputFormat - moment.js date format string/unix, used for output.
   * @returns {string}
   */
  convertUTCToLocal(date: any, dateFormat: string, utcOffset: number, outputFormat?: string) {
    let dateF = moment.utc(date, dateFormat);

    if (utcOffset !== 0) {
      dateF.add(utcOffset, 'minutes');
    }

    if (outputFormat && outputFormat.trim()) {
      date = dateF.format(outputFormat);
    } else {
      date = dateF.format(dateFormat);
    }

    return date;
  }

  convertUTCToLocalTime(date: any, format: string) {
    let stillUtc = moment.utc(date).toDate();
    return moment(stillUtc).valueOf();
  }

  getUserDateTimeFormat(): string {
    const userSession = this.userSessionRepository.getUserSession();

    if(userSession && userSession.dateConfigObject && userSession.timeConfigObject) {
      return userSession.dateConfigObject.dateTimeFormat + ' ' + userSession.timeConfigObject.dateTimeFormat;
    }

    return '';
  }
}
